import React, { useState, useEffect } from 'react';
import { PRIORITY_AR, STATUS_AR } from '../utils/labels';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TasksKanban from './TasksKanban';
import TasksCalendar from './TasksCalendar';
import TasksColumns from './TasksColumns';
import AssigneePicker from '../components/AssigneePicker';
import RepeatPicker from '../components/RepeatPicker';
import TagPicker from '../components/TagPicker';
import { TagList } from '../components/TagBadge';
import { fmtDate, fmtDateBoth, fmtMinutes } from '../utils/dates';
import { hasPermission } from '../utils/roles';

const isoAfterDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DUE_SHORTCUTS = [
  { label: 'اليوم', days: 0 },
  { label: 'غداً', days: 1 },
  { label: 'بعد أسبوع', days: 7 },
];

const ESTIMATE_SHORTCUTS = [15, 30, 60, 120, 240];

function FormSection({ icon, title, badge, open, onToggle, children }) {
  return (
    <section className={`fsec${open ? ' open' : ''}`}>
      <button type="button" className="fsec-head" onClick={onToggle} aria-expanded={open}>
        <span className="fsec-icon">{icon}</span>
        <span>{title}</span>
        {badge != null && badge !== '' && <span className="fsec-badge">{badge}</span>}
        <span className="fsec-chev">⌄</span>
      </button>
      {open && <div className="fsec-body">{children}</div>}
    </section>
  );
}

function WorkflowProgress({ subtasks }) {
  if (!subtasks || subtasks.length === 0) return null;
  const done = subtasks.filter(s => s.status === 'completed').length;
  const active = subtasks.find(s => s.status === 'in_progress');
  const pct = Math.round((done / subtasks.length) * 100);
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, height: 4, background: 'var(--gray-200)', borderRadius: 2 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? 'var(--success)' : 'var(--primary)', borderRadius: 2, transition: 'width .3s' }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{done}/{subtasks.length}</span>
      </div>
      {active && <div style={{ fontSize: 11, color: 'var(--primary)', marginTop: 2 }}>▶ {active.title}</div>}
    </div>
  );
}

function TaskListSkeleton() {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr><th>المهمة</th><th>المسؤول</th><th>الحالة</th><th>الموعد</th></tr>
        </thead>
        <tbody>
          {[...Array(6)].map((_, i) => (
            <tr key={i}>
              <td><div className="skeleton-line" style={{ width: '70%' }} /></td>
              <td><div className="skeleton-line" style={{ width: '60%' }} /></td>
              <td><div className="skeleton-line" style={{ width: 80 }} /></td>
              <td><div className="skeleton-line" style={{ width: 90 }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Tasks() {
  const { api, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(localStorage.getItem('tasks-view') || 'list');
  const [showCompleted, setShowCompleted] = useState(localStorage.getItem('tasks-show-completed') === '1');
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    assigned_to: searchParams.get('assigned_to') || '',
    client_id: '',
    sheikh_id: searchParams.get('sheikh_id') || '',
    project_id: searchParams.get('project_id') || '',
    priority: '',
    overdue: searchParams.get('overdue') || '',
    search: '',
    due_from: '',
    due_to: '',
    tag_id: '',
  });

  const loadTasks = () => {
    const params = { ...filters };
    if (showCompleted) params.include_completed = '1';
    const qs = Object.entries(params).filter(([,v]) => v).map(([k,v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    setLoading(true);
    api(`/tasks?${qs}`).then(setTasks).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTasks();
  }, [filters.status, filters.assigned_to, filters.client_id, filters.sheikh_id, filters.project_id,
      filters.priority, filters.overdue, filters.due_from, filters.due_to, filters.tag_id, showCompleted]);

  useEffect(() => {
    api('/users').then(setUsers).catch(() => {});
    api('/clients').then(setClients).catch(() => {});
    api('/projects').then(setProjects).catch(() => {});
    api('/workflows').then(setWorkflows).catch(() => {});
    api('/tags').then(setAllTags).catch(() => {});
  }, []);

  const setViewSaved = (v) => { localStorage.setItem('tasks-view', v); setView(v); };
  const toggleShowCompleted = () => {
    const nv = !showCompleted;
    setShowCompleted(nv);
    localStorage.setItem('tasks-show-completed', nv ? '1' : '0');
  };

  const handleStatusChange = async (id, status) => {
    // Optimistic update
    setTasks(ts => ts.map(t => t.id === id ? { ...t, status } : t));
    try {
      await api(`/tasks/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
    } catch (err) {
      toast.error(err.message);
      loadTasks();
    }
  };

  const handleDelete = async (e, task) => {
    e.stopPropagation();
    if (!window.confirm(`هل أنت متأكد من حذف المهمة "${task.title}"؟ سيتم حذف جميع التعليقات والمرفقات والأوقات المسجّلة والمهام الفرعية.`)) return;
    try {
      await api(`/tasks/${task.id}`, { method: 'DELETE' });
      toast.success('تم حذف المهمة');
      setTasks(ts => ts.filter(x => x.id !== task.id));
    } catch (err) { toast.error(err.message); }
  };

  const canDelete = ['admin', 'supervisor'].includes(user?.role);

  const resetFilters = () => {
    setFilters({
      status: '', assigned_to: '', client_id: '', sheikh_id: '', project_id: '', priority: '',
      overdue: '', search: '', due_from: '', due_to: '', tag_id: '',
    });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const initialForm = {
    title: '', description: '', assigned_to: '', client_id: '', sheikh_id: '', project_id: '',
    workflow_id: '', priority: 'medium', due_date: '', start_date: '',
    estimated_minutes: '', scheduled_for: '', tag_ids: [],
    bucket: '', notes: '', checklist: [], assignee_ids: [], repeat: null,
    show_checklist_on_card: true, show_notes_on_card: false,
  };
  const [form, setForm] = useState(initialForm);
  const [checklistDraft, setChecklistDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const defaultSections = { basics: true, link: true, dates: true, extra: false, advanced: false };
  const [sections, setSections] = useState(defaultSections);
  const toggleSection = (key) => setSections(s => ({ ...s, [key]: !s[key] }));

  // المستودعات المستخدمة سابقاً — تُقترح في قائمة الإكمال التلقائي
  const buckets = [...new Set(tasks.map(t => t.bucket).filter(Boolean))].sort();

  const addChecklistDraft = () => {
    const title = checklistDraft.trim();
    if (!title) return;
    setForm(f => ({ ...f, checklist: [...f.checklist, title] }));
    setChecklistDraft('');
  };

  const openModal = () => {
    setForm(initialForm);
    setChecklistDraft('');
    setSections(defaultSections);
    setShowModal(true);
  };

  const closeModal = () => {
    const dirty = JSON.stringify(form) !== JSON.stringify(initialForm) || checklistDraft.trim() !== '';
    if (dirty && !window.confirm('سيتم تجاهل ما أدخلته. هل تريد إغلاق النموذج؟')) return;
    setShowModal(false);
  };

  const createTask = async () => {
    const title = form.title.trim();
    if (!title || saving) return;
    setSaving(true);
    try {
      // عنصر قائمة الاختيار المكتوب ولم يُضَف بعد يضيع لو أُرسل النموذج بلوحة المفاتيح
      const draft = checklistDraft.trim();
      const payload = { ...form, title, checklist: draft ? [...form.checklist, draft] : form.checklist };
      const keep = ['tag_ids', 'checklist', 'assignee_ids', 'repeat', 'show_checklist_on_card', 'show_notes_on_card'];
      Object.keys(payload).forEach(k => {
        if (keep.includes(k)) return;
        if (!payload[k]) delete payload[k];
      });
      if (!payload.tag_ids || payload.tag_ids.length === 0) delete payload.tag_ids;
      if (payload.checklist.length === 0) delete payload.checklist;
      if (payload.assignee_ids.length === 0) delete payload.assignee_ids;
      if (!payload.repeat) delete payload.repeat;
      delete payload.assigned_to;
      await api('/tasks', { method: 'POST', body: JSON.stringify(payload) });
      toast.success('تم إنشاء المهمة');
      setShowModal(false);
      setForm(initialForm);
      setChecklistDraft('');
      loadTasks();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const extraCount = form.tag_ids.length + form.checklist.length + (form.bucket ? 1 : 0) + (form.notes ? 1 : 0);
  const advancedOn = Boolean(form.repeat?.frequency || form.scheduled_for);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h2>المهام</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="view-switcher" data-tour="tasks-view-modes">
            <button className={`view-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setViewSaved('list')}>📋 قائمة</button>
            <button className={`view-btn ${view === 'columns' ? 'active' : ''}`} onClick={() => setViewSaved('columns')}>📊 أعمدة</button>
            <button className={`view-btn ${view === 'kanban' ? 'active' : ''}`} onClick={() => setViewSaved('kanban')}>🗂️ Kanban</button>
            <button className={`view-btn ${view === 'calendar' ? 'active' : ''}`} onClick={() => setViewSaved('calendar')}>📅 تقويم</button>
          </div>
          {hasPermission(user, 'tasks.create') && (
            <button className="btn btn-primary" data-tour="tasks-create" onClick={openModal}>+ مهمة جديدة</button>
          )}
        </div>
      </div>

      {view !== 'calendar' && (
        <>
          <div className="filters" data-tour="tasks-filters">
            <input
              className="form-input"
              placeholder="🔍 بحث..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && loadTasks()}
              style={{ minWidth: 200 }}
            />
            <select className="form-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
              <option value="">كل الحالات</option>
              {Object.entries(STATUS_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="form-select" value={filters.assigned_to} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))}>
              <option value="">كل الفريق</option>
              <option value="me">مهامي</option>
              {users.filter(u => ['admin','supervisor','team'].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select className="form-select" value={filters.client_id} onChange={e => setFilters(f => ({ ...f, client_id: e.target.value }))}>
              <option value="">كل العملاء</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className="form-select" value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}>
              <option value="">كل الأولويات</option>
              {Object.entries(PRIORITY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={filters.overdue === '1'} onChange={e => setFilters(f => ({ ...f, overdue: e.target.checked ? '1' : '' }))} />
              متأخرة
            </label>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }} title="إظهار المهام المكتملة والملغاة">
              <input type="checkbox" checked={showCompleted} onChange={toggleShowCompleted} />
              إظهار المنجزة
            </label>
            <button className="btn btn-outline btn-sm" onClick={() => setShowAdvanced(s => !s)}>
              {showAdvanced ? '▲' : '▼'} فلترة متقدمة
            </button>
            {activeFilterCount > 0 && (
              <button className="btn btn-outline btn-sm" onClick={resetFilters}>✖ مسح ({activeFilterCount})</button>
            )}
          </div>

          {showAdvanced && (
            <div className="card" style={{ padding: 14, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <div>
                  <label className="form-label">مشروع</label>
                  <select className="form-select" value={filters.project_id} onChange={e => setFilters(f => ({ ...f, project_id: e.target.value }))}>
                    <option value="">كل المشاريع</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">شيخ</label>
                  <select className="form-select" value={filters.sheikh_id} onChange={e => setFilters(f => ({ ...f, sheikh_id: e.target.value }))}>
                    <option value="">كل المشايخ</option>
                    {clients.filter(c => c.type === 'sheikh').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">من تاريخ</label>
                  <input type="date" className="form-input" value={filters.due_from} onChange={e => setFilters(f => ({ ...f, due_from: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">إلى تاريخ</label>
                  <input type="date" className="form-input" value={filters.due_to} onChange={e => setFilters(f => ({ ...f, due_to: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">وسم</label>
                  <select className="form-select" value={filters.tag_id} onChange={e => setFilters(f => ({ ...f, tag_id: e.target.value }))}>
                    <option value="">كل الوسوم</option>
                    {allTags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {view === 'calendar' ? (
        <TasksCalendar />
      ) : view === 'kanban' ? (
        loading ? (
          <div className="kanban-board">
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton-card" style={{ height: 400 }} />)}
          </div>
        ) : (
          <TasksKanban tasks={tasks} onStatusChange={handleStatusChange} />
        )
      ) : view === 'columns' ? (
        loading ? <TaskListSkeleton /> : <TasksColumns tasks={tasks} />
      ) : (
        <div className="card">
          {loading ? <TaskListSkeleton /> : tasks.length === 0 ? (
            <div className="empty"><div className="empty-icon">📋</div>لا توجد مهام</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>المهمة</th>
                    <th>المسار / التقدم</th>
                    <th>المسؤول</th>
                    <th>العميل</th>
                    <th>الحالة</th>
                    <th>الأولوية</th>
                    <th>الموعد</th>
                    <th>الوقت</th>
                    {canDelete && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {tasks.map(t => {
                    const overdue = t.due_date && new Date(t.due_date) < new Date() && !['completed', 'cancelled'].includes(t.status);
                    return (
                      <tr key={t.id} onClick={() => navigate(`/tasks/${t.id}`)} style={{ cursor: 'pointer' }}>
                        <td style={{ fontWeight: 500 }}>
                          {t.title}
                          {t.tags?.length > 0 && <TagList tags={t.tags} style={{ marginTop: 4 }} />}
                          {t.start_date && new Date(t.start_date) > new Date() && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                              ▶ يبدأ: {fmtDateBoth(t.start_date)}
                            </div>
                          )}
                        </td>
                        <td style={{ minWidth: 140 }}>
                          {t.workflow ? (
                            <div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>{t.workflow.name}</div>
                              <WorkflowProgress subtasks={t.subtasks} />
                            </div>
                          ) : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>يدوية</span>}
                        </td>
                        <td>{t.assignee?.name || '—'}</td>
                        <td>{t.client?.name || '—'}</td>
                        <td><span className={`badge badge-${t.status}`}>{STATUS_AR[t.status]}</span></td>
                        <td><span className={`badge badge-${t.priority}`}>{PRIORITY_AR[t.priority]}</span></td>
                        <td style={{ fontSize: 13, color: overdue ? 'var(--danger)' : 'inherit', fontWeight: overdue ? 600 : 400 }}>
                          {t.due_date ? fmtDateBoth(t.due_date) : '—'}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {t.logged_minutes > 0
                            ? `${Math.floor(t.logged_minutes / 60)}س ${t.logged_minutes % 60}د`
                            : '—'}
                          {t.estimated_minutes > 0 && (
                            <div style={{ fontSize: 10 }}>من {Math.floor(t.estimated_minutes / 60)}س</div>
                          )}
                        </td>
                        {canDelete && (
                          <td onClick={e => e.stopPropagation()}>
                            <button className="btn btn-sm btn-danger" onClick={e => handleDelete(e, t)} title="حذف المهمة">حذف</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal modal-lg modal-form"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Escape') { e.stopPropagation(); closeModal(); }
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); createTask(); }
            }}
          >
            <div className="modal-head">
              <span className="modal-head-icon">✚</span>
              <div>
                <div className="modal-title">مهمة جديدة</div>
                <div className="modal-sub">العنوان وحده يكفي للإنشاء — بقية الحقول اختيارية</div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal} title="إغلاق">✕</button>
            </div>

            <div className="modal-body">
              <FormSection
                icon="📝"
                title="الأساسيات"
                open={sections.basics}
                onToggle={() => toggleSection('basics')}
              >
                <div className="form-group">
                  <label className="form-label">عنوان المهمة <span className="req">*</span></label>
                  <input
                    className="form-input input-lead"
                    autoFocus
                    placeholder="ما الذي يجب إنجازه؟"
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">الوصف</label>
                  <textarea
                    className="form-textarea"
                    placeholder="تفاصيل تساعد المكلَّف على البدء (اختياري)"
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">الأولوية</label>
                  <div className="seg-group">
                    {Object.entries(PRIORITY_AR).map(([k, v]) => (
                      <button
                        key={k}
                        type="button"
                        className={`seg-opt seg-${k}${form.priority === k ? ' on' : ''}`}
                        onClick={() => setForm(f => ({ ...f, priority: k }))}
                      >{v}</button>
                    ))}
                  </div>
                </div>
              </FormSection>

              <FormSection
                icon="👥"
                title="الإسناد والربط"
                badge={form.assignee_ids.length || null}
                open={sections.link}
                onToggle={() => toggleSection('link')}
              >
                <div className="form-group">
                  <label className="form-label">المكلَّفون</label>
                  <AssigneePicker
                    value={form.assignee_ids}
                    onChange={ids => setForm(f => ({ ...f, assignee_ids: ids }))}
                    users={users}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">المشروع</label>
                    <select className="form-select" value={form.project_id} onChange={e => {
                      const pid = e.target.value;
                      setForm(f => {
                        const wf = workflows.find(w => String(w.id) === String(f.workflow_id));
                        const keepWf = wf && (!wf.project_id || String(wf.project_id) === String(pid));
                        return { ...f, project_id: pid, workflow_id: keepWf ? f.workflow_id : '' };
                      });
                    }}>
                      <option value="">بدون مشروع</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">العميل / الشيخ</label>
                    <select className="form-select" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value, sheikh_id: e.target.value }))}>
                      <option value="">بدون عميل</option>
                      {(() => {
                        if (!form.project_id) return clients;
                        const proj = projects.find(p => String(p.id) === String(form.project_id));
                        return proj?.sheikhs || [];
                      })().map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">المسار</label>
                  <select className="form-select" value={form.workflow_id} onChange={e => {
                    const wid = e.target.value;
                    const wf = workflows.find(w => String(w.id) === String(wid));
                    const email = wf?.steps?.[0]?.default_assignee;
                    const u = email ? users.find(x => x.email === email) : null;
                    setForm(f => ({ ...f, workflow_id: wid, assignee_ids: u && f.assignee_ids.length === 0 ? [u.id] : f.assignee_ids }));
                  }}>
                    <option value="">بدون مسار (يدوية)</option>
                    {workflows
                      .filter(w => !w.project_id || String(w.project_id) === String(form.project_id))
                      .map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                  <div className="form-hint">المسار يولّد خطوات المهمة الفرعية تلقائياً.</div>
                </div>
              </FormSection>

              <FormSection
                icon="🗓️"
                title="المواعيد والوقت"
                badge={form.due_date ? fmtDate(form.due_date) : null}
                open={sections.dates}
                onToggle={() => toggleSection('dates')}
              >
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">تاريخ البدء</label>
                    <input className="form-input" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">تاريخ التسليم</label>
                    <input className="form-input" type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
                    <div className="chip-row">
                      {DUE_SHORTCUTS.map(s => {
                        const iso = isoAfterDays(s.days);
                        return (
                          <button
                            key={s.label}
                            type="button"
                            className={`chip${form.due_date === iso ? ' on' : ''}`}
                            onClick={() => setForm(f => ({ ...f, due_date: f.due_date === iso ? '' : iso }))}
                          >{s.label}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">الوقت المقدّر (دقائق)</label>
                  <input className="form-input" type="number" min="0" placeholder="0" value={form.estimated_minutes} onChange={e => setForm(f => ({ ...f, estimated_minutes: e.target.value }))} />
                  <div className="chip-row">
                    {ESTIMATE_SHORTCUTS.map(m => (
                      <button
                        key={m}
                        type="button"
                        className={`chip${String(form.estimated_minutes) === String(m) ? ' on' : ''}`}
                        onClick={() => setForm(f => ({ ...f, estimated_minutes: String(f.estimated_minutes) === String(m) ? '' : String(m) }))}
                      >{fmtMinutes(m)}</button>
                    ))}
                  </div>
                </div>
              </FormSection>

              <FormSection
                icon="🏷️"
                title="التنظيم والتفاصيل"
                badge={extraCount || null}
                open={sections.extra}
                onToggle={() => toggleSection('extra')}
              >
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">الوسوم</label>
                    <TagPicker value={form.tag_ids} onChange={ids => setForm(f => ({ ...f, tag_ids: ids }))} allTags={allTags} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">🗂️ المستودع</label>
                    <input
                      className="form-input"
                      list="task-buckets"
                      placeholder="مثال: العمليات"
                      value={form.bucket}
                      onChange={e => setForm(f => ({ ...f, bucket: e.target.value }))}
                    />
                    <datalist id="task-buckets">
                      {buckets.map(b => <option key={b} value={b} />)}
                    </datalist>
                  </div>
                </div>
                <div className="form-group">
                  <div className="checklist-head">
                    <label className="form-label" style={{ margin: 0 }}>
                      ☑️ قائمة الاختيار{form.checklist.length > 0 ? ` (${form.checklist.length} عناصر)` : ''}
                    </label>
                    <label className="board-toggle">
                      <input
                        type="checkbox"
                        checked={form.show_checklist_on_card}
                        onChange={e => setForm(f => ({ ...f, show_checklist_on_card: e.target.checked }))}
                      />
                      عرض في طريقة عرض اللوحة
                    </label>
                  </div>
                  {form.checklist.map((item, i) => (
                    <div key={i} className="checklist-row">
                      <span className="checklist-dot" />
                      <span style={{ flex: 1 }}>{item}</span>
                      <button
                        type="button"
                        className="checklist-remove"
                        title="حذف العنصر"
                        onClick={() => setForm(f => ({ ...f, checklist: f.checklist.filter((_, j) => j !== i) }))}
                      >✕</button>
                    </div>
                  ))}
                  <input
                    className="form-input"
                    placeholder="إضافة عنصر ثم Enter"
                    value={checklistDraft}
                    onChange={e => setChecklistDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChecklistDraft(); } }}
                    onBlur={addChecklistDraft}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <div className="checklist-head">
                    <label className="form-label" style={{ margin: 0 }}>📝 ملاحظات</label>
                    <label className="board-toggle">
                      <input
                        type="checkbox"
                        checked={form.show_notes_on_card}
                        onChange={e => setForm(f => ({ ...f, show_notes_on_card: e.target.checked }))}
                      />
                      عرض في طريقة عرض اللوحة
                    </label>
                  </div>
                  <textarea
                    className="form-textarea"
                    placeholder="اكتب وصفاً أو أضف ملاحظات هنا"
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  />
                </div>
              </FormSection>

              <FormSection
                icon="⚙️"
                title="التكرار والجدولة"
                badge={advancedOn ? 'مفعّل' : null}
                open={sections.advanced}
                onToggle={() => toggleSection('advanced')}
              >
                <div className="form-group">
                  <label className="form-label">🔁 تكرار</label>
                  <RepeatPicker value={form.repeat} onChange={r => setForm(f => ({ ...f, repeat: r }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">⏰ ظهور مجدول</label>
                  <input
                    className="form-input"
                    type="datetime-local"
                    value={form.scheduled_for}
                    onChange={e => setForm(f => ({ ...f, scheduled_for: e.target.value }))}
                  />
                  <div className="form-hint">
                    اتركه فارغاً لظهور فوري. المهمة ستُخفى عن الفريق حتى يحين الموعد، والمدير فقط يرى المهام المجدولة.
                  </div>
                </div>
              </FormSection>
            </div>

            <div className="modal-foot">
              <button className="btn btn-primary" onClick={createTask} disabled={!form.title.trim() || saving}>
                {saving ? 'جارٍ الإنشاء…' : 'إنشاء المهمة'}
              </button>
              <button className="btn btn-outline" onClick={closeModal} disabled={saving}>إلغاء</button>
              <span className="modal-foot-hint">
                {form.title.trim() ? 'Ctrl + Enter للإنشاء السريع' : 'العنوان مطلوب للمتابعة'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
